import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { wipeAllDrafts } from "@/lib/drafts";
import { tagSentryUser, track } from "@/lib/sentry";

const AuthContext = createContext(null);

// Cached user profile (NOT auth credentials — the JWT lives in an httpOnly cookie).
// We keep the profile in localStorage so first paint doesn't flash "logged out"
// while /auth/me round-trips. Real auth still requires the cookie to be valid.
// codeql[js/clear-text-storage-of-sensitive-data]: cache only contains non-sensitive
// profile fields (id, name, role, agency_id). No tokens. No PII beyond name.
const PROFILE_CACHE_KEY = "hc_user_profile_cache";

// Migrate older clients that wrote under the previous, shorter key.
try {
  const legacy = localStorage.getItem("hc_user");
  if (legacy && !localStorage.getItem(PROFILE_CACHE_KEY)) {
    localStorage.setItem(PROFILE_CACHE_KEY, legacy);
  }
  if (legacy) localStorage.removeItem("hc_user");
} catch {
  /* ignore */
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      // codeql[js/clear-text-storage-of-sensitive-data]: see key declaration
      const raw = localStorage.getItem(PROFILE_CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  const persist = useCallback((u) => {
    try {
      // codeql[js/clear-text-storage-of-sensitive-data]: non-sensitive profile
      localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(u));
    } catch (_) { /* storage quota or disabled */ }
    setUser(u);
    tagSentryUser(u);
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // If 2FA is enrolled, the backend doesn't set a cookie — it returns a
      // short-lived challenge token instead. The Login page handles the OTP step.
      if (data && data.requires_2fa) {
        return { requires_2fa: true, challenge_token: data.challenge_token, ttl_seconds: data.ttl_seconds };
      }
      // Multi-agency user — backend returns the picker challenge. Login page handles it.
      if (data && data.requires_agency_select) {
        return {
          requires_agency_select: true,
          challenge_token: data.challenge_token,
          ttl_seconds: data.ttl_seconds,
          agencies: data.agencies || [],
        };
      }
      persist(data);
      track("login_success", { method: "password" });
      return data;
    } finally {
      setLoading(false);
    }
  }, [persist]);

  const submit2fa = useCallback(async (challenge_token, code) => {
    const { data } = await api.post("/auth/login/2fa", { challenge_token, code });
    if (data && data.requires_agency_select) {
      // 2FA succeeded but user has multiple agencies — still need picker.
      return {
        requires_agency_select: true,
        challenge_token: data.challenge_token,
        ttl_seconds: data.ttl_seconds,
        agencies: data.agencies || [],
      };
    }
    persist(data);
    track("login_success", { method: "2fa" });
    return data;
  }, [persist]);

  const selectAgency = useCallback(async (challenge_token, agency_id) => {
    const { data } = await api.post("/auth/login/select-agency", { challenge_token, agency_id });
    persist(data);
    track("login_success", { method: "agency_select" });
    return data;
  }, [persist]);

  // In-app switcher (already-authenticated). Mints a fresh JWT scoped to
  // `agency_id` and updates the cached profile. Caller is expected to reload
  // any agency-scoped data (or the page) after this resolves.
  const switchAgency = useCallback(async (agency_id) => {
    const { data } = await api.post("/auth/switch-agency", { agency_id });
    persist(data);
    track("agency_switched", { agency_id });
    return data;
  }, [persist]);

  const logout = useCallback(async () => {
    // Wipe PHI-bearing local data on logout (shared-device safety)
    wipeAllDrafts();
    try {
      localStorage.removeItem(PROFILE_CACHE_KEY);
    } catch (_) { /* ignore */ }
    setUser(null);
    tagSentryUser(null);
    try {
      await api.post("/auth/logout");
    } catch (_) {
      // Even if the server call fails, we've already cleared local state.
    }
  }, []);

  // On mount, always reconcile against the server. If the cookie is gone or
  // expired, we drop any stale cached profile so the user is bounced to /login.
  // Skip on public pages — anonymous visitors landing on /login, /forgot-password
  // etc don't need an /auth/me probe (it just generates noisy 401s in console).
  useEffect(() => {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const PUBLIC_PREFIXES = [
      "/",  // marketing landing
      "/login",
      "/signup",
      "/agency/signup",
      "/accept-invite",
      "/family",
      "/agency/",
      "/share/",
      "/reset-password/",
      "/invite/",
      "/forgot-password",
      "/apply/",
      "/privacy",
      "/terms",
      "/pricing",
    ];
    if (PUBLIC_PREFIXES.some((p) => path.startsWith(p))) {
      return;
    }
    let cancelled = false;
    api
      .get("/auth/me")
      .then(({ data }) => {
        if (cancelled) return;
        try {
          // codeql[js/clear-text-storage-of-sensitive-data]: non-sensitive profile
          localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(data));
        } catch (_) {}
        setUser(data);
        tagSentryUser(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          try {
            localStorage.removeItem(PROFILE_CACHE_KEY);
          } catch (_) {}
          setUser(null);
          tagSentryUser(null);
        }
        // Other errors (network blip) — keep cached user so the app stays usable.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, submit2fa, selectAgency, switchAgency, setUser: persist }),
    [user, loading, login, logout, submit2fa, selectAgency, switchAgency, persist]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
